/*
 * O mapa do tópico, logo abaixo do primeiro post: uma faixa entre duas linhas com os números em destaque
 * (visualizações, curtidas, usuários) e, ao lado, os avatares de quem mais
 * participou. Curtidas e usuários abrem um menu ("Respostas mais curtidas",
 * "Autores frequentes"). Em tópicos longos ele se repete no fim.
 *
 * Só o menu das visualizações faz requisição (os dias do gráfico, ao abrir).
 * Visualizações só aparecem quando alguma extensão já manda o atributo; usuários e curtidas são contados dos posts, mas só
 * quando todos já estão na memória — uma soma parcial seria um número errado
 * com cara de certo. Sem eles, entra o número de respostas, que o Flarum
 * sempre tem.
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import classList from 'flarum/common/utils/classList';
import username from 'flarum/common/helpers/username';
import { forumBool, isExtensionEnabled, t } from '../../utils/settings';
import { formatViews, viewsLabel } from '../../utils/views';
import TopicViewsMenu, { prefetchTopicViews } from '../../features/stats/views/TopicViewsMenu';
import { discussionLinks, trackClick } from '../../features/links/data';
/** Quantos avatares cabem na faixa, e quantos posts no menu de curtidas. */
const POSTERS_LIMIT = 5;
const LIKED_LIMIT = 3;
/** A partir de quantas respostas o mapa se repete no fim. */
const BOTTOM_MIN_REPLIES = 10;
/** Quantos links o menu "Links populares" mostra antes do "Mostrar mais". */
const LINKS_LIMIT = 5;
function numericAttribute(discussion, names) {
  for (const name of names) {
    const value = discussion.attribute(name);
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

/**
 * Os posts já carregados do tópico, numa passada só pelo store (o mapa roda a
 * cada redesenho, e aparece duas vezes nos tópicos longos): `all` diz se são
 * todos (para os totais) ou só parte (os avatares podem ser parciais).
 */
function loadedPosts(discussion) {
  const posts = [];
  let all = true;
  for (const id of discussion.postIds()) {
    const post = app.store.getById('posts', id);
    if (post) posts.push(post);else all = false;
  }
  return {
    posts,
    all
  };
}
const VIEW_ATTRIBUTES = ['viewCount', 'views', 'view_count'];

/** Sem respostas nem visualizações o mapa só repetiria o cabeçalho do post. */
export function hasTopicMap(discussion) {
  return discussion.replyCount() > 0 || numericAttribute(discussion, VIEW_ATTRIBUTES) !== null;
}
export function hasBottomTopicMap(discussion) {
  return discussion.replyCount() >= BOTTOM_MIN_REPLIES;
}
function likesOf(post) {
  return Number(post.attribute('likesCount')) || 0;
}

/** Quem mais postou, primeiro; empate fica com quem postou antes. */
function posters(posts) {
  const counts = new Map();
  posts.forEach(post => {
    const user = post.user();
    if (user) counts.set(user, (counts.get(user) || 0) + 1);
  });
  return Array.from(counts.entries()).map(([user, count]) => ({
    user,
    count
  })).sort((a, b) => b.count - a.count);
}
export default class TopicMap extends Component {
  open = null;
  onremove(vnode) {
    super.onremove(vnode);
    this.unlisten();
  }
  view() {
    const discussion = this.attrs.discussion;
    const position = this.attrs.position || 'op';
    const views = numericAttribute(discussion, VIEW_ATTRIBUTES);
    const loaded = loadedPosts(discussion);
    const visible = loaded.posts.filter(post => post.contentType() === 'comment' && !post.isHidden());
    const top = posters(visible);
    // Os totais só com todos os posts na mão; os avatares servem com parte.
    const comments = loaded.all ? visible : null;
    const users = numericAttribute(discussion, ['participantCount', 'participantsCount']) ?? (comments ? top.length : null);
    const likes = numericAttribute(discussion, ['canelaLikeCount']) ?? (comments && isExtensionEnabled('flarum-likes') ? comments.reduce((sum, post) => sum + likesOf(post), 0) : null);
    const stats = [];

    // O tópico aberto agora nunca tem zero
    // visualizações. O número abre o gráfico "Visualizações recentes" quando
    // as visualizações são as do tema (o recurso stats tem os dias).
    const shownViews = views === null ? null : Math.max(views, 1);
    const viewsMenu = forumBool('canelaStatsViewsEnabled', false) ? 'views' : undefined;
    if (shownViews !== null) stats.push(this.stat('views', shownViews, viewsLabel(shownViews), viewsMenu));
    if (likes) stats.push(this.stat('likes', likes, t('topic.map.likes', {
      count: likes
    }), 'likes'));

    // "N links" entre curtidas e usuários (recurso `links`).
    const links = discussionLinks(discussion);
    if (links && links.total > 0) stats.push(this.stat('links', links.total, t('topic.map.links', {
      count: links.total
    }), 'links'));
    if (users !== null && users > 0) stats.push(this.stat('users', users, t('topic.map.users', {
      count: users
    }), 'users'));
    if (users === null) {
      const replies = discussion.replyCount();
      stats.push(this.stat('replies', replies, t('topic.map.replies', {
        count: replies
      })));
    }
    return m("section", {
      className: classList('CanelaTopicMap', `CanelaTopicMap--${position}`),
      "aria-label": t('topic.map.aria_label')
    }, m("div", {
      className: classList('CanelaTopicMap-stats', {
        'CanelaTopicMap-stats--single': stats.length === 1
      })
    }, stats, top.length > 1 && m("div", {
      className: "CanelaTopicMap-users"
    }, top.slice(0, POSTERS_LIMIT).map(({
      user,
      count
    }) => m(Link, {
      className: user.isOnline() ? 'CanelaTopicMap-poster CanelaTopicMap-poster--online' : 'CanelaTopicMap-poster',
      href: app.route.user(user),
      title: `${user.displayName()}: ${t('topic.map.posts', {
        count
      })}`
    }, m(Avatar, {
      user: user
    }))))), this.open === 'views' && shownViews !== null && this.viewsMenu(discussion, shownViews), this.open === 'likes' && this.likesMenu(visible), this.open === 'links' && links && this.linksMenu(discussion, links.top), this.open === 'users' && this.usersMenu(top));
  }
  stat(key, value, label, menu) {
    const content = [
    // O número curto: 1,2k, 120k, 1,0M.
    m("span", {
      className: "CanelaTopicMap-number number"
    }, formatViews(value)), m("span", {
      className: "CanelaTopicMap-label"
    }, label)];
    if (!menu) return m("div", {
      className: `CanelaTopicMap-stat CanelaTopicMap-stat--${key}`
    }, content);
    return m("button", {
      type: "button",
      className: classList('CanelaTopicMap-stat', `CanelaTopicMap-stat--${key}`, {
        active: this.open === menu
      }),
      "aria-expanded": this.open === menu ? 'true' : 'false',
      onclick: event => this.toggle(menu, event.currentTarget),
      onpointerenter: menu === 'views' ? event => this.prefetchViews(event, value) : undefined,
      onfocus: menu === 'views' ? event => this.prefetchViews(event, value) : undefined
    }, content);
  }

  /** O menu de visualizações abre já com os números (TopicViewsMenu). */
  prefetchViews(event, views) {
    event.redraw = false;
    prefetchTopicViews(this.attrs.discussion, views);
  }
  toggle(menu, trigger) {
    if (this.open === menu) {
      this.close();
      return;
    }
    this.unlisten();
    this.open = menu;
    this.anchorLeft = trigger.offsetLeft;

    // Fecha ao clicar fora ou com Esc, como os outros menus.
    this.closer = event => {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
      if (event.type === 'mousedown' && event.target.closest?.('.CanelaTopicMap-menu, .CanelaTopicMap-stat')) return;
      this.close();
      m.redraw();
    };
    document.addEventListener('mousedown', this.closer, true);
    document.addEventListener('keydown', this.closer, true);
  }
  anchorLeft = 0;
  close() {
    this.open = null;
    this.unlisten();
  }
  unlisten() {
    if (!this.closer) return;
    document.removeEventListener('mousedown', this.closer, true);
    document.removeEventListener('keydown', this.closer, true);
    this.closer = undefined;
  }
  menu(className, title, body) {
    return m("div", {
      className: classList('CanelaTopicMap-menu', className),
      role: "dialog",
      "aria-label": title,
      style: {
        left: `${this.anchorLeft}px`
      }
    }, m("h3", {
      className: "CanelaTopicMap-menuTitle"
    }, title), body);
  }

  /** "Visualizações recentes": o gráfico por dia (ou os números, com até dois dias). */
  viewsMenu(discussion, views) {
    return this.menu('CanelaTopicMap-menu--views topic-map__views-content', t('stats.views.menu_title'), m(TopicViewsMenu, {
      discussion: discussion,
      views: views
    }));
  }

  /** As respostas mais curtidas (o primeiro post não conta). */
  likesMenu(posts) {
    const liked = posts.filter(post => post.number() > 1 && likesOf(post) > 0).sort((a, b) => likesOf(b) - likesOf(a) || a.number() - b.number()).slice(0, LIKED_LIMIT);
    return this.menu('CanelaTopicMap-menu--likes', t('topic.map.most_liked'), m("ul", {
      className: "CanelaTopicMap-liked"
    }, liked.map(post => m("li", null, m(Link, {
      href: app.route.post(post),
      onclick: () => this.close()
    }, m("span", {
      className: "CanelaTopicMap-likedUser"
    }, m(Avatar, {
      user: post.user() || null
    }), username(post.user() || null)), m("span", {
      className: "CanelaTopicMap-likedCount"
    }, abbreviateNumber(likesOf(post)), m(Icon, {
      name: "fas fa-heart"
    })), m("p", {
      className: "CanelaTopicMap-likedExcerpt"
    }, post.contentPlain()?.slice(0, 200)))))));
  }

  /** Mostra todos os links no menu, e não só os primeiros. */
  allLinks = false;

  /** "Links populares": cliques, título (ou endereço) e domínio, os mais clicados primeiro. */
  linksMenu(discussion, links) {
    const shown = this.allLinks ? links : links.slice(0, LINKS_LIMIT);
    return this.menu('CanelaTopicMap-menu--links', t('topic.map.popular_links'), [m("ul", {
      className: "CanelaTopicMap-links"
    }, shown.map(link => m("li", {
      className: "CanelaTopicMap-link"
    }, m("span", {
      className: "CanelaTopicMap-linkCount"
    }, link.clicks > 0 && m("span", {
      className: "CanelaTopicMap-linkClicks badge clicks",
      title: t('links.clicks', {
        count: link.clicks
      })
    }, formatViews(link.clicks))), m("a", {
      className: "CanelaTopicMap-linkTitle",
      href: link.url,
      target: "_blank",
      rel: "noopener nofollow ugc",
      title: link.url,
      onclick: () => trackClick(link.postId, link.url, discussion),
      onauxclick: event => event.button === 1 && trackClick(link.postId, link.url, discussion)
    }, link.title || link.url.replace(/^https?:\/\//, '')), m("span", {
      className: "CanelaTopicMap-linkDomain"
    }, link.domain.replace(/^www\./, ''))))), links.length > LINKS_LIMIT && !this.allLinks ? m("button", {
      type: "button",
      className: "Button Button--link CanelaTopicMap-linksMore",
      onclick: () => this.allLinks = true
    }, t('topic.map.show_more_links')) : null]);
  }
  usersMenu(top) {
    return this.menu('CanelaTopicMap-menu--users', t('topic.map.frequent_posters'), m("div", {
      className: "CanelaTopicMap-usersList"
    }, top.slice(0, 24).map(({
      user,
      count
    }) => m(Link, {
      className: "CanelaTopicMap-poster",
      href: app.route.user(user),
      title: user.displayName(),
      onclick: () => this.close()
    }, m(Avatar, {
      user: user
    }), count > 1 && m("span", {
      className: "CanelaTopicMap-postCount"
    }, count)))));
  }
}
flarum.reg.add('ramon-canela', 'forum/topic/components/TopicMap', TopicMap);